import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Filter as FilterOff,
  Flame,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Eye,
  Ticket as TicketIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useListTickets, useTicketCounters } from "./api";
import {
  TICKET_CATEGORIA_LABEL,
  TICKET_ESTADO_LABEL,
  TICKET_PRIORIDAD_LABEL,
  ticketCategoriaEnum,
  ticketPrioridadEnum,
  type EstadoGrupo,
  type TicketCategoria,
  type TicketPrioridad,
  type TicketSummary,
  type TicketsFilters,
} from "./schemas";
import { NewTicketDialog } from "./NewTicketDialog";
import { useDebounce } from "@/hooks/useDebounce";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

// ── Helpers de presentación ─────────────────────────────────────────────
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "ahora";
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `hace ${d} día${d === 1 ? "" : "s"}`;
  if (d < 30) return `hace ${Math.floor(d / 7)} sem`;
  return `hace ${Math.floor(d / 30)} mes`;
}

const PILL_PRIO: Record<TicketPrioridad, string> = {
  baja: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  normal: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  alta: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  urgente: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
};
const PILL_ESTADO: Record<string, string> = {
  abierto: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  en_proceso: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  resuelto: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  cerrado: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};
const PILL_CAT: Record<TicketCategoria, string> = {
  soporte_tecnico: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  facturacion: "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  instalacion: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  cancelacion: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  otro: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};

function avatarFromName(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Tabs ────────────────────────────────────────────────────────────────
const TABS: { key: EstadoGrupo | "abierto" | "en_proceso" | "resuelto" | "cerrado"; label: string }[] = [
  { key: "abierto", label: "Abiertos" },
  { key: "en_proceso", label: "En proceso" },
  { key: "resuelto", label: "Resueltos" },
  { key: "cerrado", label: "Cerrados" },
  { key: "todos", label: "Todos" },
];

// ── Componente principal ────────────────────────────────────────────────
export function TicketsListPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<typeof TABS[number]["key"]>("abierto");
  const [categoria, setCategoria] = useState<TicketCategoria | "">("");
  const [prioridad, setPrioridad] = useState<TicketPrioridad | "">("");
  const [asignacion, setAsignacion] = useState<"" | "mi" | "sin">("");
  const [busquedaInput, setBusquedaInput] = useState("");
  const busqueda = useDebounce(busquedaInput, 300);
  const [page, setPage] = useState(1);
  const [openNew, setOpenNew] = useState(false);
  const isMobile = useIsMobile(); // rc137 — tarjetas en vez de la rejilla

  const filters = useMemo<TicketsFilters>(() => {
    const f: TicketsFilters = { page, per_page: 25 };
    if (tab === "todos") {
      // sin filtro de estado
    } else if (tab === "abierto" || tab === "en_proceso" || tab === "resuelto" || tab === "cerrado") {
      f.estado = tab;
    }
    if (categoria) f.categoria = categoria;
    if (prioridad) f.prioridad = prioridad;
    if (asignacion === "mi") f.asignados_a_mi = true;
    if (asignacion === "sin") f.sin_asignar = true;
    if (busqueda.trim()) f.busqueda = busqueda.trim();
    return f;
  }, [tab, categoria, prioridad, asignacion, busqueda, page]);

  const { data, isLoading, isFetching } = useListTickets(filters);
  const { data: counters } = useTicketCounters();

  function clearFilters() {
    setCategoria("");
    setPrioridad("");
    setAsignacion("");
    setBusquedaInput("");
    setPage(1);
  }

  const tabCount = (key: typeof TABS[number]["key"]): number | undefined => {
    if (!counters) return undefined;
    if (key === "abierto") return counters.abiertos_total;
    if (key === "en_proceso") return counters.en_proceso_total;
    return undefined;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Topbar */}
      {/* rc137 — móvil: la cabecera se parte en dos filas (título + Nuevo / búsqueda ancha) */}
      <header className="h-12 flex items-center px-4 gap-3 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex-shrink-0 max-md:h-auto max-md:flex-wrap max-md:px-3 max-md:py-2 max-md:gap-2">
        <div className="flex items-center gap-2 text-zinc-900 dark:text-zinc-100">
          <TicketIcon size={18} />
          <span className="font-semibold text-sm">Tickets</span>
        </div>
        {counters && (
          <>
            <span className="text-zinc-300 dark:text-zinc-700">·</span>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              {counters.abiertos_total + counters.en_proceso_total} abiertos
            </span>
            <span className="text-zinc-300 dark:text-zinc-700 max-md:hidden">·</span>
            <span className="text-xs text-zinc-500 dark:text-zinc-400 max-md:hidden">
              {counters.abiertos_asignados_a_mi} asignados a mí
            </span>
          </>
        )}

        <div className="ml-auto flex items-center gap-2 max-md:w-full max-md:ml-0">
          <div className="relative max-md:flex-1">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Buscar ticket o cliente…"
              value={busquedaInput}
              onChange={(e) => {
                setBusquedaInput(e.target.value);
                setPage(1);
              }}
              className="w-64 text-xs px-3 py-1.5 pl-7 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-600 max-md:w-full"
            />
          </div>
          <button
            type="button"
            onClick={() => setOpenNew(true)}
            className="bg-black hover:bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 max-md:shrink-0"
          >
            <Plus size={14} />
            <span className="max-md:hidden">Nuevo ticket</span>
            <span className="md:hidden">Nuevo</span>
          </button>
        </div>
      </header>

      {/* Tabs por estado + filtros */}
      {/* rc137 — móvil: tira horizontal que scrollea (sin wrap), selects compactos */}
      <div className="px-4 py-2 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-1.5 bg-white dark:bg-zinc-950 flex-wrap flex-shrink-0 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-3 max-md:[&>*]:shrink-0 max-md:[scrollbar-width:none]">
        {TABS.map((t) => {
          const count = tabCount(t.key);
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 transition-colors",
                active
                  ? "bg-black text-white"
                  : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800",
              )}
            >
              <span>{t.label}</span>
              {count !== undefined && (
                <span
                  className={cn(
                    "rounded-full px-1.5 text-[10px] font-semibold",
                    active
                      ? "bg-white/20 text-white"
                      : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300",
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}

        <div className="w-px h-5 bg-zinc-200 dark:bg-zinc-800 mx-1" />

        <select
          value={categoria}
          onChange={(e) => {
            setCategoria(e.target.value as TicketCategoria | "");
            setPage(1);
          }}
          className="text-xs px-2 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200"
        >
          <option value="">Categoría: todas</option>
          {ticketCategoriaEnum.map((c) => (
            <option key={c} value={c}>
              {TICKET_CATEGORIA_LABEL[c]}
            </option>
          ))}
        </select>

        <select
          value={prioridad}
          onChange={(e) => {
            setPrioridad(e.target.value as TicketPrioridad | "");
            setPage(1);
          }}
          className="text-xs px-2 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200"
        >
          <option value="">Prioridad: todas</option>
          {ticketPrioridadEnum.map((p) => (
            <option key={p} value={p}>
              {TICKET_PRIORIDAD_LABEL[p]}
            </option>
          ))}
        </select>

        <select
          value={asignacion}
          onChange={(e) => {
            setAsignacion(e.target.value as "" | "mi" | "sin");
            setPage(1);
          }}
          className="text-xs px-2 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200"
        >
          <option value="">Asignación: cualquiera</option>
          <option value="mi">Asignados a mí</option>
          <option value="sin">Sin asignar</option>
        </select>

        {(categoria || prioridad || asignacion || busquedaInput) && (
          <button
            type="button"
            onClick={clearFilters}
            className="ml-1 px-2 py-1.5 rounded-md text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-1"
            title="Limpiar filtros"
          >
            <FilterOff size={14} />
            Limpiar
          </button>
        )}
      </div>

      {/* Lista — rc137: en móvil tarjetas (MobileCard), en desktop la rejilla */}
      {isMobile ? (
        <div className="flex-1 overflow-y-auto p-3">
          {isLoading && !data && (
            <div className="p-8 text-center text-sm text-zinc-500">Cargando tickets…</div>
          )}
          {data && data.items.length === 0 && (
            <MobileEmpty>No hay tickets que coincidan con los filtros.</MobileEmpty>
          )}
          <MobileList>
            {data?.items.map((t) => (
              <TicketCard key={t.id} ticket={t} />
            ))}
          </MobileList>
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto">
        {/* Header tabla */}
        <div className="grid grid-cols-[44px_1.4fr_2.5fr_130px_110px_1.2fr_110px_105px_36px] gap-3 px-4 py-2 bg-zinc-50 dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
          <div></div>
          <div>Cliente</div>
          <div>Asunto</div>
          <div>Categoría</div>
          <div>Prioridad</div>
          <div>Asignado</div>
          <div>Estado</div>
          <div>Última act.</div>
          <div></div>
        </div>

        {isLoading && !data && (
          <div className="p-8 text-center text-sm text-zinc-500">Cargando tickets…</div>
        )}

        {data && data.items.length === 0 && (
          <div className="p-12 text-center">
            <TicketIcon size={32} className="mx-auto mb-3 text-zinc-300" />
            <div className="text-sm text-zinc-500">No hay tickets que coincidan con los filtros.</div>
          </div>
        )}

        {data?.items.map((t) => (
          <TicketRow
            key={t.id}
            ticket={t}
            onClick={() => navigate(`/tickets/${t.id}`)}
          />
        ))}
      </div>
      )}

      {/* Paginación */}
      {data && data.total > data.por_pagina && (
        <footer className="border-t border-zinc-200 dark:border-zinc-800 px-4 py-2 flex items-center justify-between text-xs text-zinc-500 bg-white dark:bg-zinc-950 flex-shrink-0">
          <div>
            <span className="max-md:hidden">Mostrando </span>{data.items.length} de {data.total}<span className="max-md:hidden"> tickets</span>
            {isFetching && <span className="ml-2 text-zinc-400">· refrescando…</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <span>
              Pág {page} de {Math.ceil(data.total / data.por_pagina)}
            </span>
            <button
              type="button"
              onClick={() =>
                setPage((p) => (p < Math.ceil(data.total / data.por_pagina) ? p + 1 : p))
              }
              disabled={page >= Math.ceil(data.total / data.por_pagina)}
              className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </footer>
      )}

      <NewTicketDialog open={openNew} onClose={() => setOpenNew(false)} />
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────
function TicketRow({ ticket, onClick }: { ticket: TicketSummary; onClick: () => void }) {
  const isUrgente = ticket.prioridad === "urgente" && ticket.estado !== "cerrado";
  return (
    <div
      onClick={onClick}
      className={cn(
        "grid grid-cols-[44px_1.4fr_2.5fr_130px_110px_1.2fr_110px_105px_36px] gap-3 px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-900 cursor-pointer transition-colors",
        "hover:bg-zinc-50 dark:hover:bg-zinc-900",
        isUrgente && "bg-rose-50/40 dark:bg-rose-950/20 border-l-2 border-l-rose-500",
        ticket.estado === "cerrado" && "opacity-60",
      )}
    >
      <div className="text-[10px] font-mono text-zinc-500 self-center">
        #{ticket.codigo.replace("TKT-", "")}
      </div>
      <div className="self-center">
        <div className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate">
          {ticket.cliente.razon_social}
        </div>
        <div className="text-[10px] font-mono text-zinc-500">{ticket.cliente.codigo}</div>
      </div>
      <div className="self-center min-w-0">
        <div className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate">
          {ticket.asunto}
        </div>
        {ticket.creado_por_cliente && (
          <div className="text-[10px] text-zinc-500 mt-0.5">via portal cliente</div>
        )}
      </div>
      <div className="self-center">
        <span
          className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
            PILL_CAT[ticket.categoria],
          )}
        >
          {TICKET_CATEGORIA_LABEL[ticket.categoria]}
        </span>
      </div>
      <div className="self-center">
        <span
          className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
            PILL_PRIO[ticket.prioridad],
          )}
        >
          {ticket.prioridad === "urgente" && <Flame size={10} />}
          {TICKET_PRIORIDAD_LABEL[ticket.prioridad]}
        </span>
      </div>
      <div className="self-center">
        {ticket.asignado ? (
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-violet-500 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
              {avatarFromName(ticket.asignado.nombre)}
            </div>
            <span className="text-xs text-zinc-700 dark:text-zinc-300 truncate">
              {ticket.asignado.nombre}
            </span>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs italic text-amber-700 dark:text-amber-400">
            <UserPlus size={12} />
            Sin asignar
          </span>
        )}
      </div>
      <div className="self-center">
        <span
          className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
            PILL_ESTADO[ticket.estado],
          )}
        >
          {TICKET_ESTADO_LABEL[ticket.estado]}
        </span>
      </div>
      <div className="self-center text-[11px] text-zinc-500">
        {relativeTime(ticket.ultimo_mensaje_at)}
      </div>
      <div className="self-center text-zinc-400">
        <Eye size={14} />
      </div>
    </div>
  );
}

// ── Tarjeta móvil (rc137) ────────────────────────────────────────────────
function TicketCard({ ticket }: { ticket: TicketSummary }) {
  const isUrgente = ticket.prioridad === "urgente" && ticket.estado !== "cerrado";
  return (
    <MobileCard
      to={`/tickets/${ticket.id}`}
      accent={isUrgente}
      className={cn(ticket.estado === "cerrado" && "opacity-60")}
      title={ticket.asunto}
      subtitle={
        <>
          <span className="font-mono">#{ticket.codigo.replace("TKT-", "")}</span> · {ticket.cliente.razon_social}
          {ticket.creado_por_cliente && <span className="ml-1 text-zinc-400">· portal</span>}
        </>
      }
      right={
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium", PILL_ESTADO[ticket.estado])}>
          {TICKET_ESTADO_LABEL[ticket.estado]}
        </span>
      }
    >
      <div className="col-span-2 flex flex-wrap items-center gap-1.5">
        <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium", PILL_PRIO[ticket.prioridad])}>
          {ticket.prioridad === "urgente" && <Flame size={10} />}
          {TICKET_PRIORIDAD_LABEL[ticket.prioridad]}
        </span>
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium", PILL_CAT[ticket.categoria])}>
          {TICKET_CATEGORIA_LABEL[ticket.categoria]}
        </span>
      </div>
      <MobileMeta label="Asignado">
        {ticket.asignado ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 text-[8px] font-bold text-white">
              {avatarFromName(ticket.asignado.nombre)}
            </span>
            {ticket.asignado.nombre}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 italic text-amber-700 dark:text-amber-400">
            <UserPlus size={11} /> Sin asignar
          </span>
        )}
      </MobileMeta>
      <MobileMeta label="Última act.">{relativeTime(ticket.ultimo_mensaje_at)}</MobileMeta>
    </MobileCard>
  );
}
